import React, { useState, useMemo } from 'react';
import HabitCalendarConflictModal from './HabitCalendarConflictModal';
import { ChevronLeft, Flame, TrendingUp, Target, Award, Trash2, Pause, Play, Archive, Plus, Minus, Info, X, Clock, Zap, Settings, CalendarDays, Layers } from 'lucide-react';
import { calculateStreak, calculateBestStreak, completionRate, calculateStability, getLocalDateStr, addDays, isHabitDueOnDate, formatDuration } from '../../lib/habitUtils';
import HabitRewards from './HabitRewards';
import SchichtTagsModal from './SchichtTagsModal';
import { userStorage } from '../../utils/userStorage';
const CUE_PRESETS = [
  'Nach dem Aufstehen', 'Nach dem Kaffee', 'Nach dem Mittagessen',
  'Vor dem Schlafen', 'Nach dem Duschen', 'In der Mittagspause',
  'Nach der Arbeit', 'Morgens um 7'
];
const WEEKDAY_LABELS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const SCHICHT_TYPES_D = ['Früh', 'Spät', 'Nacht', 'Frei'];
const SCHICHT_BTN_D = {
  Früh:  'bg-orange-100 text-orange-800 border-orange-300',
  Spät:  'bg-blue-100 text-blue-800 border-blue-300',
  Nacht: 'bg-purple-100 text-purple-800 border-purple-300',
  Frei:  'bg-teal-100 text-teal-700 border-teal-300',
};
const DEFAULT_SCHICHT_ZEITEN_D = {
  Früh:  { enabled: false, type: 'alle', calendarBlock: false, time: '16:00', duration: 30, months: 3 },
  Spät:  { enabled: false, type: 'alle', calendarBlock: false, time: '09:00', duration: 30, months: 3 },
  Nacht: { enabled: false, type: 'alle', calendarBlock: false, time: '13:00', duration: 30, months: 3 },
  Frei:  { enabled: false, type: 'alle', calendarBlock: false, time: '10:00', duration: 30, months: 3 },
};

export default function HabitDetail({ habit, logs, onBack, onUpdate, onDelete, habits = [] }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showProgression, setShowProgression] = useState(false);
  const [showStabilityInfo, setShowStabilityInfo] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editCue, setEditCue] = useState(habit.ifThen?.cueCustom || '');
  const [editReminderEnabled, setEditReminderEnabled] = useState(habit.notifications?.reminderEnabled || false);
  const [editReminderTime, setEditReminderTime] = useState(habit.notifications?.reminderTime || '08:00');
  const [editCalendarEnabled, setEditCalendarEnabled] = useState(habit.calendarReminder?.enabled || false);
  const [editCalendarDuration, setEditCalendarDuration] = useState(habit.calendarReminder?.durationMinutes || 15);
  const [editConsolidationMonths, setEditConsolidationMonths] = useState(habit.calendarReminder?.consolidationMonths || 3);
  const [editCalendarTime, setEditCalendarTime] = useState(habit.calendarReminder?.time || habit.notifications?.reminderTime || '08:00');
  const [showConsolidationInfo, setShowConsolidationInfo] = useState(false);
  const [calendarSaveError, setCalendarSaveError] = useState('');
  const [editMinTarget, setEditMinTarget] = useState(habit.tracking?.minimumTarget || 1);
  const [editCurTarget, setEditCurTarget] = useState(habit.tracking?.currentTarget || 1);
  const [editGoalTarget, setEditGoalTarget] = useState(habit.tracking?.goalTarget || '');
  const [editUnitLabel, setEditUnitLabel] = useState(habit.tracking?.unitLabel || '');
  const [newTarget, setNewTarget] = useState(habit.tracking?.currentTarget || 0);
  // Schichten-specific calendar editing
  const [editSchichtZeiten, setEditSchichtZeiten] = useState(
    habit.schedule?.schichtZeiten || { ...DEFAULT_SCHICHT_ZEITEN_D }
  );
  const [editSchichtCalEnabled, setEditSchichtCalEnabled] = useState(
    Object.values(habit.schedule?.schichtZeiten || {}).some(z => z?.calendarBlock)
  );
  const [showSchichtTagsInDetail, setShowSchichtTagsInDetail] = useState(false);
  const [schichtTagsTargetInDetail, setSchichtTagsTargetInDetail] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictModalForShift, setConflictModalForShift] = useState(null);
  const [conflictRefreshKey, setConflictRefreshKey] = useState(0);
  const [pendingSkippedDates, setPendingSkippedDates] = useState([]);
  const [pendingDailyTimeOverrides, setPendingDailyTimeOverrides] = useState({});
  const [pendingAptChanges, setPendingAptChanges] = useState({ updates: {}, deletes: [] });
  const [conflictsFullyResolved, setConflictsFullyResolved] = useState(false);
  const [resolvedShifts, setResolvedShifts] = useState(new Set());

  const stats = useMemo(() => {
    const streak = calculateStreak(habit, logs);
    const bestStreak = calculateBestStreak(habit, logs);
    const rate7 = completionRate(habit, logs, 7);
    const rate30 = completionRate(habit, logs, 30);
    const stability = calculateStability(habit, logs);
    const totalDone = logs.filter(l => l.done).length;
    const daysSinceStart = Math.round(
      (new Date(getLocalDateStr() + 'T12:00:00') - new Date(habit.startDate + 'T12:00:00')) / 86400000
    );
    return { streak, bestStreak, rate7, rate30, stability, totalDone, daysSinceStart };
  }, [habit, logs]);

  // Calendar heatmap (last 8 weeks)
  const heatmap = useMemo(() => {
    const today = getLocalDateStr();
    const weeks = [];
    const logMap = {};
    logs.forEach(l => { logMap[l.date] = l; });

    for (let w = 7; w >= 0; w--) {
      const week = [];
      for (let d = 1; d <= 7; d++) {
        const dayOffset = -(w * 7 + (7 - d));
        const dt = addDays(today, dayOffset);
        const log = logMap[dt];
        const isDue = dt >= habit.startDate && dt <= today && isHabitDueOnDate(habit, dt);
        week.push({ date: dt, done: log?.done || false, future: dt > today, isDue });
      }
      weeks.push(week);
    }
    return weeks;
  }, [logs, habit]);

  // Schedule display text
  const scheduleText = useMemo(() => {
    const s = habit.schedule;
    if (!s) return 'Täglich';
    if (s.frequencyType === 'daily') return 'Täglich';
    if (s.frequencyType === 'weekly') return `${s.weeklyTargetCount || 3}x pro Woche`;
    if (s.frequencyType === 'custom' && s.daysOfWeek?.length > 0) {
      const labels = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
      return s.daysOfWeek.sort((a, b) => a - b).map(d => labels[d]).join(', ');
    }
    return 'Täglich';
  }, [habit]);

  // Real-time Konflikt-Prüfung: Wochenkalender + andere Habits
  const calendarConflict = useMemo(() => {
    const freq = habit.schedule?.frequencyType;
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;

    // Schichten: jede aktivierte Schicht mit Kalender-Block prüfen
    if (freq === 'schichten') {
      try {
        const apts = JSON.parse(userStorage.getItem('zielplanung_appointments') || '[]');
        for (const s of SCHICHT_TYPES_D) {
          const z = editSchichtZeiten[s];
          if (!z?.calendarBlock || !z.time) continue;
          const [sH, sM] = z.time.split(':').map(Number);
          const newStart = sH * 60 + sM;
          const newEnd = newStart + (z.duration || 30);
          for (const apt of apts) {
            // Nur die eigenen Kalenderblöcke ausschließen – andere Habits' Blöcke prüfen
            if (apt.isHabitReminder && apt.habitId === habit.id) continue;
            if (!apt.startTime || !apt.endTime) continue;
            if (apt.date && apt.date < todayStr) continue;
            const [aH, aM] = apt.startTime.split(':').map(Number);
            const [eH, eM] = apt.endTime.split(':').map(Number);
            if (newStart >= eH * 60 + eM || aH * 60 + aM >= newEnd) continue;
            return `${s}schicht: Zeitkonflikt mit „${apt.title || 'Termin'}"`;
          }
        }
      } catch {}
      return null;
    }

    if (!editCalendarEnabled || freq === 'weekly' || !editCalendarTime) return null;
    const [sH, sM] = editCalendarTime.split(':').map(Number);
    const newStart = sH * 60 + sM;
    const newEnd = newStart + (editCalendarDuration || 15);
    const myDays = habit.schedule?.daysOfWeek || [];

    // Andere Habits prüfen
    for (const h of habits) {
      if (h.id === habit.id || !h.calendarReminder?.enabled) continue;
      if (h.status === 'archived' || h.status === 'paused') continue;
      const cr = h.calendarReminder;
      if (!cr.time) continue;
      const [hH, hM] = cr.time.split(':').map(Number);
      const hStart = hH * 60 + hM;
      const hEnd = hStart + (cr.durationMinutes || 15);
      if (newStart >= hEnd || hStart >= newEnd) continue;
      const hFreq = h.schedule?.frequencyType;
      const hDays = h.schedule?.daysOfWeek || [];
      let overlap = false;
      if (freq === 'daily' && hFreq === 'daily') overlap = true;
      else if (freq === 'daily' && hFreq === 'custom') overlap = hDays.length > 0;
      else if (freq === 'custom' && hFreq === 'daily') overlap = myDays.length > 0;
      else if (freq === 'custom' && hFreq === 'custom') overlap = myDays.some(d => hDays.includes(d));
      if (overlap) return `Zeitkonflikt mit Habit „${h.title}"`;
    }

    // Wochenkalender-Termine prüfen
    try {
      const apts = JSON.parse(userStorage.getItem('zielplanung_appointments') || '[]');
      for (const apt of apts) {
        // Nur die eigenen Kalenderblöcke ausschließen – andere Habits' Blöcke prüfen
        if (apt.isHabitReminder && apt.habitId === habit.id) continue;
        if (!apt.startTime || !apt.endTime) continue;
        if (apt.date && apt.date < todayStr) continue;
        const [aH, aM] = apt.startTime.split(':').map(Number);
        const [eH, eM] = apt.endTime.split(':').map(Number);
        if (newStart >= eH * 60 + eM || aH * 60 + aM >= newEnd) continue;
        if (freq === 'custom' && myDays.length > 0) {
          const aptDate = new Date((apt.date || apt.from || '') + 'T12:00:00');
          if (isNaN(aptDate.getTime())) continue;
          if (!myDays.includes(aptDate.getDay())) continue;
        }
        return `Zeitkonflikt mit Termin „${apt.title || 'Termin'}"`;
      }
    } catch {}

    return null;
  }, [editCalendarEnabled, editCalendarTime, editCalendarDuration, editSchichtZeiten, habit, habits, conflictRefreshKey]); // eslint-disable-line

  const handleStatusChange = (newStatus) => {
    onUpdate({ ...habit, status: newStatus });
  };

  const handleProgression = () => {
    onUpdate({
      ...habit,
      tracking: { ...habit.tracking, currentTarget: newTarget }
    });
    setShowProgression(false);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h2 className="text-lg font-bold text-slate-800">{habit.title}</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              habit.status === 'new' ? 'bg-emerald-100 text-emerald-700' :
              habit.status === 'maintenance' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
            }`}>
              {habit.status === 'new' ? 'Aufbau' : habit.status === 'maintenance' ? 'Routine' : habit.status === 'paused' ? 'Pausiert' : 'Archiviert'}
            </span>
            <span className="text-xs text-slate-400">seit {habit.startDate}</span>
            <span className="flex items-center gap-1 text-xs font-semibold text-slate-500">
              <CalendarDays className="w-3.5 h-3.5 text-slate-400" />
              {formatDuration(habit.startDate)}
            </span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl p-3 border border-slate-100">
          <div className="flex items-center gap-1.5 mb-1">
            <Flame className="w-4 h-4 text-orange-500" />
            <span className="text-xs text-slate-500">Streak</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{stats.streak}</p>
          <p className="text-xs text-slate-400">Bester: {stats.bestStreak}</p>
        </div>
        <div className="bg-white rounded-xl p-3 border border-slate-100">
          <div className="flex items-center gap-1.5 mb-1">
            <Target className="w-4 h-4 text-emerald-500" />
            <span className="text-xs text-slate-500">Rate (7T)</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{stats.rate7}%</p>
          <p className="text-xs text-slate-400">30T: {stats.rate30}%</p>
        </div>
        <div className="bg-white rounded-xl p-3 border border-slate-100">
          <div className="flex items-center gap-1.5 mb-1">
            <Award className="w-4 h-4 text-purple-500" />
            <span className="text-xs text-slate-500">Insgesamt</span>
          </div>
          <p className="text-2xl font-bold text-slate-800">{stats.totalDone}</p>
          <p className="text-xs text-slate-400">{stats.totalDone * 10} XP</p>
        </div>
        <div className="bg-white rounded-xl p-3 border border-slate-100 relative">
          <div className="flex items-center gap-1.5 mb-1">
            <TrendingUp className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-slate-500">Stabilität</span>
            <button
              onClick={() => setShowStabilityInfo(!showStabilityInfo)}
              className="p-0.5 text-slate-300 hover:text-blue-500 transition-colors"
              data-testid="stability-info-btn"
            >
              <Info className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="text-2xl font-bold text-slate-800">{stats.stability.score}</p>
          {stats.stability.canPromote && habit.status === 'new' && (
            <button
              onClick={() => handleStatusChange('maintenance')}
              className="text-xs text-emerald-600 font-medium hover:underline"
            >
              Zur Routine?
            </button>
          )}
          {habit.status === 'maintenance' && (
            <button
              onClick={() => handleStatusChange('new')}
              className="text-xs text-blue-500 font-medium hover:underline"
              data-testid="revert-to-new-btn"
            >
              Zurück zu Aufbau
            </button>
          )}
          {showStabilityInfo && (
            <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-slate-200 rounded-xl p-4 shadow-lg" data-testid="stability-info-popup">
              <div className="flex items-start justify-between mb-2">
                <h4 className="text-sm font-semibold text-slate-700">Was bedeutet Stabilität?</h4>
                <button onClick={() => setShowStabilityInfo(false)} className="p-0.5 text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="text-xs text-slate-500 space-y-2">
                <p>Stabilität beschreibt, wie gefestigt eine Gewohnheit ist. Sie basiert auf:</p>
                <ul className="space-y-1 pl-3">
                  <li className="flex items-start gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                    <span>deiner Erfüllungsquote</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                    <span>der Regelmäßigkeit</span>
                  </li>
                  <li className="flex items-start gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                    <span>und der Dauer, seit du sie begonnen hast</span>
                  </li>
                </ul>
                <p className="text-slate-400 italic">Je stabiler eine Gewohnheit, desto weniger Willenskraft brauchst du – sie wird Teil deines Alltags.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Calendar Heatmap */}
      <div className="bg-white rounded-xl p-4 border border-slate-100">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Letzte 8 Wochen</h3>
        <div className="flex gap-1">
          {['M', 'D', 'M', 'D', 'F', 'S', 'S'].map((d, i) => (
            <div key={i} className="flex-1 text-center text-[10px] text-slate-400 mb-1">{d}</div>
          ))}
        </div>
        <div className="space-y-1">
          {heatmap.map((week, wi) => (
            <div key={wi} className="flex gap-1">
              {week.map((day, di) => (
                <div
                  key={di}
                  className={`flex-1 aspect-square rounded-sm ${
                    day.future ? 'bg-slate-50' :
                    day.done ? 'bg-emerald-500' :
                    day.isDue ? 'bg-red-300/60' :
                    'bg-slate-100'
                  }`}
                  title={`${day.date}${day.isDue ? ' (fällig)' : ' (nicht fällig)'}${day.done ? ' - erledigt' : ''}`}
                />
              ))}
            </div>
          ))}
        </div>
        {/* Legend + Schedule */}
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-50">
          <div className="flex items-center gap-3 text-[10px] text-slate-400">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />Erledigt</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-300/60" />Fällig</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-slate-100" />Frei</span>
          </div>
          <span className="text-[10px] text-slate-400">{scheduleText}</span>
        </div>
      </div>

      {/* Progression (Performance only) */}
      {habit.type === 'performance' && (
        <div className="bg-white rounded-xl p-4 border border-slate-100">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Progression</h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Aktuell: <strong>{habit.tracking?.currentTarget} {habit.tracking?.unitLabel}</strong></p>
              {habit.tracking?.goalTarget && (
                <p className="text-xs text-slate-400">Ziel: {habit.tracking.goalTarget} {habit.tracking.unitLabel}</p>
              )}
            </div>
            <button
              onClick={() => { setNewTarget(habit.tracking?.currentTarget || 1); setShowProgression(true); }}
              className="px-3 py-1.5 bg-blue-50 text-blue-600 text-sm font-medium rounded-lg hover:bg-blue-100"
              data-testid="progression-btn"
            >
              Steigern
            </button>
          </div>

          {showProgression && (
            <div className="mt-3 p-3 bg-blue-50 rounded-lg space-y-2">
              <div className="flex items-center justify-center gap-3">
                <button onClick={() => setNewTarget(Math.max(1, newTarget - 1))} className="w-8 h-8 rounded-full bg-white border flex items-center justify-center">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="text-xl font-bold text-slate-800">{newTarget}</span>
                <button onClick={() => setNewTarget(newTarget + 1)} className="w-8 h-8 rounded-full bg-white border flex items-center justify-center">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="flex gap-2 justify-center">
                {[1, 2, 5].map(n => (
                  <button key={n} onClick={() => setNewTarget((habit.tracking?.currentTarget || 1) + n)}
                    className="px-2 py-1 bg-white border rounded text-xs text-slate-600 hover:bg-slate-50">+{n}</button>
                ))}
              </div>
              <p className="text-xs text-center text-slate-500">{habit.tracking?.currentTarget} → {newTarget} {habit.tracking?.unitLabel}</p>
              <button onClick={handleProgression} className="w-full py-2 bg-blue-600 text-white text-sm font-medium rounded-lg">
                Speichern
              </button>
            </div>
          )}
        </div>
      )}

      {/* Rewards */}
      <HabitRewards habit={habit} logs={logs} onUpdate={onUpdate} />

      {/* Settings: Trigger / 1%-Setup / Reminders */}
      <div className="bg-white rounded-xl p-4 border border-slate-100">
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="w-full flex items-center justify-between"
          data-testid="toggle-settings"
        >
          <div className="flex items-center gap-2">
            <Settings className="w-4 h-4 text-slate-400" />
            <h3 className="text-sm font-semibold text-slate-700">Einstellungen</h3>
          </div>
          <ChevronLeft className={`w-4 h-4 text-slate-400 transition-transform ${showSettings ? '-rotate-90' : 'rotate-180'}`} />
        </button>

        {showSettings && (
          <div className="mt-3 space-y-4 pt-3 border-t border-slate-100">
            {/* Trigger */}
            <div>
              <label className="text-xs font-medium text-slate-500">Trigger (If-Then)</label>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {CUE_PRESETS.map(c => (
                  <button key={c} onClick={() => setEditCue(editCue === c ? '' : c)}
                    className={`px-2 py-0.5 rounded-full text-[10px] transition-all ${
                      editCue === c ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'
                    }`}>{c}</button>
                ))}
              </div>
              <input type="text" value={CUE_PRESETS.includes(editCue) ? '' : editCue}
                onChange={e => setEditCue(e.target.value)} placeholder="Eigenen Trigger..."
                className="w-full mt-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-xs" />
            </div>

            {/* 1%-Setup (Performance only) */}
            {habit.type === 'performance' && (
              <div>
                <label className="text-xs font-medium text-slate-500">1%-Setup</label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  <div>
                    <span className="text-[10px] text-slate-400">Einheit</span>
                    <input type="text" value={editUnitLabel} onChange={e => setEditUnitLabel(e.target.value)}
                      className="w-full px-2 py-1 border border-slate-200 rounded-lg text-xs" />
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400">Endziel</span>
                    <input type="number" value={editGoalTarget} onChange={e => setEditGoalTarget(e.target.value)}
                      className="w-full px-2 py-1 border border-slate-200 rounded-lg text-xs" />
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400">Minimum (1%)</span>
                    <input type="number" min={1} value={editMinTarget}
                      onChange={e => { setEditMinTarget(Number(e.target.value)); if (editCurTarget < Number(e.target.value)) setEditCurTarget(Number(e.target.value)); }}
                      className="w-full px-2 py-1 border border-slate-200 rounded-lg text-xs" />
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400">Aktuelles Ziel</span>
                    <input type="number" min={editMinTarget} value={editCurTarget}
                      onChange={e => setEditCurTarget(Number(e.target.value))}
                      className="w-full px-2 py-1 border border-slate-200 rounded-lg text-xs" />
                  </div>
                </div>
              </div>
            )}

            {/* Reminders & Kalender-Block */}
            <div className="space-y-2">
              {/* Push-Erinnerung */}
              <div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-xs font-medium text-slate-500">Push-Erinnerung</span>
                  </div>
                  <button
                    onClick={() => setEditReminderEnabled(!editReminderEnabled)}
                    className={`w-9 h-5 rounded-full transition-colors ${editReminderEnabled ? 'bg-emerald-500' : 'bg-slate-200'}`}
                  >
                    <div className={`w-3.5 h-3.5 bg-white rounded-full shadow transform transition-transform ${editReminderEnabled ? 'translate-x-4.5 ml-[18px]' : 'translate-x-0.5 ml-[2px]'}`} />
                  </button>
                </div>
                {editReminderEnabled && (
                  <input type="time" value={editReminderTime} onChange={e => setEditReminderTime(e.target.value)}
                    className="mt-1.5 px-2 py-1 border border-slate-200 rounded-lg text-xs" />
                )}
              </div>
              {/* Kalender-Block */}
              <div>
                {habit.schedule?.frequencyType === 'schichten' ? (
                  /* ── Schichten: per-shift Kalender-Block-Einstellungen ── */
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Layers className="w-3.5 h-3.5 text-violet-500" />
                        <span className="text-xs font-medium text-slate-500">Kalender-Blöcke (Schichten)</span>
                      </div>
                      <button
                        onClick={() => {
                          const newVal = !editSchichtCalEnabled;
                          setEditSchichtCalEnabled(newVal);
                          if (!newVal) {
                            setEditSchichtZeiten(prev => {
                              const next = {};
                              Object.keys(prev).forEach(s => { next[s] = prev[s] ? { ...prev[s], calendarBlock: false } : prev[s]; });
                              return next;
                            });
                          }
                        }}
                        className={`w-9 h-5 rounded-full transition-colors ${editSchichtCalEnabled ? 'bg-blue-500' : 'bg-slate-200'}`}
                      >
                        <div className={`w-3.5 h-3.5 bg-white rounded-full shadow transform transition-transform ${editSchichtCalEnabled ? 'ml-[18px]' : 'ml-[2px]'}`} />
                      </button>
                    </div>

                    {editSchichtCalEnabled && (
                      <div className="space-y-2 pt-1">
                        <p className="text-[10px] text-slate-500">An welchen Schichten?</p>
                        {/* Shift-Buttons */}
                        <div className="grid grid-cols-2 gap-1.5">
                          {SCHICHT_TYPES_D.map(s => {
                            const isInRhythm = s === 'Frei'
                              ? habit.schedule?.schichtRhythmus?.days?.includes(null)
                              : habit.schedule?.schichtRhythmus?.days?.includes(s);
                            const z = editSchichtZeiten[s] || {};
                            return (
                              <button key={s} type="button"
                                disabled={!isInRhythm}
                                onClick={() => setEditSchichtZeiten(prev => ({
                                  ...prev,
                                  [s]: { ...(DEFAULT_SCHICHT_ZEITEN_D[s]), ...(prev[s] || {}), enabled: !prev[s]?.enabled }
                                }))}
                                className={`px-2 py-1.5 rounded-lg border text-[10px] font-semibold transition-all ${
                                  !isInRhythm
                                    ? 'opacity-30 cursor-not-allowed bg-slate-50 border-slate-200 text-slate-400'
                                    : z.enabled
                                      ? SCHICHT_BTN_D[s]
                                      : 'bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100'
                                }`}
                              >
                                {s}schicht
                              </button>
                            );
                          })}
                        </div>

                        {/* Per-Shift-Einstellungen */}
                        {SCHICHT_TYPES_D.filter(s => editSchichtZeiten[s]?.enabled).map(s => {
                          const z = editSchichtZeiten[s] || {};
                          const cyclePositions = (habit.schedule?.schichtRhythmus?.days || [])
                            .map((d, i) => ({ d: d ?? 'Frei', i }))
                            .filter(({ d }) => d === s);
                          return (
                            <div key={s} className="rounded-xl border border-slate-200 bg-slate-50 p-2.5 space-y-1.5">
                              <p className="text-[10px] font-semibold text-slate-700">{s}schicht</p>
                              {/* Alle / Bestimmte */}
                              <div className="flex gap-1.5">
                                {['alle', 'bestimmte'].map(t => (
                                  <button key={t} type="button"
                                    onClick={() => setEditSchichtZeiten(prev => ({
                                      ...prev, [s]: { ...prev[s], type: t,
                                        enabledCyclePositions: t === 'bestimmte' ? cyclePositions.map(c => c.i) : []
                                      }
                                    }))}
                                    className={`px-2 py-0.5 rounded text-[10px] font-semibold border transition-colors ${
                                      z.type === t
                                        ? 'bg-violet-100 border-violet-300 text-violet-800'
                                        : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-100'
                                    }`}
                                  >
                                    {t === 'alle' ? 'Alle' : 'Bestimmte'}
                                  </button>
                                ))}
                              </div>
                              {/* Bestimmte Tage auswählen */}
                              {z.type === 'bestimmte' && cyclePositions.length > 0 && habit.schedule?.schichtRhythmus && (
                                <button type="button"
                                  onClick={() => { setSchichtTagsTargetInDetail(s); setShowSchichtTagsInDetail(true); }}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded bg-violet-100 border border-violet-300 text-violet-800 text-[10px] font-semibold hover:bg-violet-200 transition-colors"
                                >
                                  <Layers className="w-3 h-3" />
                                  Tage auswählen
                                  {(z.enabledCyclePositions || []).length > 0 && (
                                    <span className="bg-violet-600 text-white rounded-full px-1 py-0.5 text-[9px] leading-none">
                                      {(z.enabledCyclePositions || []).length}/{cyclePositions.length}
                                    </span>
                                  )}
                                </button>
                              )}
                              {/* Kalender-Block toggle */}
                              <div className="flex items-center justify-between pt-1 border-t border-slate-200">
                                <div className="flex items-center gap-1.5">
                                  <Clock className="w-3 h-3 text-slate-400" />
                                  <span className="text-[10px] text-slate-500">Kalender-Block</span>
                                </div>
                                <button type="button"
                                  onClick={() => setEditSchichtZeiten(prev => ({
                                    ...prev, [s]: { ...prev[s], calendarBlock: !prev[s]?.calendarBlock }
                                  }))}
                                  className={`w-7 h-3.5 rounded-full transition-colors ${z.calendarBlock ? 'bg-blue-500' : 'bg-slate-200'}`}
                                >
                                  <div className={`w-3 h-3 bg-white rounded-full shadow transform transition-transform ${z.calendarBlock ? 'ml-[14px]' : 'ml-[1px]'}`} />
                                </button>
                              </div>
                              {/* Zeit / Dauer / Monate */}
                              {z.calendarBlock && (
                                <div className="space-y-1 p-1.5 bg-blue-50 rounded border border-blue-100">
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-slate-500 w-12">Uhrzeit</span>
                                    <input type="time" value={z.time || '08:00'}
                                      onChange={e => { setEditSchichtZeiten(prev => ({ ...prev, [s]: { ...prev[s], time: e.target.value } })); setResolvedShifts(prev => { const n = new Set(prev); n.delete(s); return n; }); }}
                                      className="px-1.5 py-0.5 border border-slate-200 rounded text-[10px] bg-white" />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-slate-500 w-12">Dauer</span>
                                    <input type="number" min={5} max={240} step={5} value={z.duration || 30}
                                      onChange={e => setEditSchichtZeiten(prev => ({ ...prev, [s]: { ...prev[s], duration: Number(e.target.value) } }))}
                                      className="w-12 px-1.5 py-0.5 border border-slate-200 rounded text-[10px] text-center bg-white" />
                                    <span className="text-[10px] text-slate-400">Min.</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-slate-500 w-12">Zeitraum</span>
                                    <input type="number" min={1} max={24} value={z.months || 3}
                                      onChange={e => setEditSchichtZeiten(prev => ({ ...prev, [s]: { ...prev[s], months: Number(e.target.value) } }))}
                                      className="w-12 px-1.5 py-0.5 border border-slate-200 rounded text-[10px] text-center bg-white" />
                                    <span className="text-[10px] text-slate-400">Mon.</span>
                                  </div>
                                  {/* Konflikt-Warnung für diese Schicht */}
                                  {(() => {
                                    try {
                                      const apts = JSON.parse(userStorage.getItem('zielplanung_appointments') || '[]');
                                      const [sH2, sM2] = (z.time || '08:00').split(':').map(Number);
                                      const ns = sH2 * 60 + sM2;
                                      const ne = ns + (z.duration || 30);
                                      for (const apt of apts) {
                                        if (apt.isHabitReminder) continue;
                                        if (!apt.startTime || !apt.endTime) continue;
                                        const [aH2, aM2] = apt.startTime.split(':').map(Number);
                                        const [eH2, eM2] = apt.endTime.split(':').map(Number);
                                        if (ns >= eH2 * 60 + eM2 || aH2 * 60 + aM2 >= ne) continue;
                                        if (resolvedShifts.has(s)) {
                                          return (
                                            <div className="flex items-center gap-1 p-1 bg-green-50 rounded border border-green-200">
                                              <span className="text-[9px] text-green-700 font-semibold">✓ Konflikte gelöst</span>
                                            </div>
                                          );
                                        }
                                        return (
                                          <div className="flex items-center gap-1 p-1 bg-red-50 rounded border border-red-200">
                                            <Info className="w-3 h-3 text-red-500 flex-shrink-0" />
                                            <p className="text-[9px] text-red-600 flex-1">Konflikt mit „{apt.title || 'Termin'}"</p>
                                            <button type="button"
                                              onClick={() => { setConflictModalForShift(s); setShowConflictModal(true); }}
                                              className="flex-shrink-0 text-[10px] font-bold text-white bg-blue-600 hover:bg-blue-700 px-2.5 py-1.5 rounded-lg shadow-sm transition-colors">
                                              Lösen
                                            </button>
                                          </div>
                                        );
                                      }
                                    } catch {}
                                    return null;
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  /* ── Standard (täglich / bestimmte Tage) ── */
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-blue-400" />
                        <span className={`text-xs font-medium ${habit.schedule?.frequencyType === 'weekly' ? 'text-slate-300' : 'text-slate-500'}`}>Kalender-Block hinzufügen</span>
                        {habit.schedule?.frequencyType === 'weekly' && (
                          <span className="text-[10px] text-slate-400 bg-slate-100 px-1 py-0.5 rounded">n.v. bei x/Woche</span>
                        )}
                      </div>
                      <button
                        onClick={() => { if (habit.schedule?.frequencyType !== 'weekly') { setEditCalendarEnabled(!editCalendarEnabled); setCalendarSaveError(''); } }}
                        disabled={habit.schedule?.frequencyType === 'weekly'}
                        className={`w-9 h-5 rounded-full transition-colors ${habit.schedule?.frequencyType === 'weekly' ? 'bg-slate-100 cursor-not-allowed' : editCalendarEnabled ? 'bg-blue-500' : 'bg-slate-200'}`}
                        data-testid="edit-calendar-toggle"
                      >
                        <div className={`w-3.5 h-3.5 bg-white rounded-full shadow transform transition-transform ${editCalendarEnabled && habit.schedule?.frequencyType !== 'weekly' ? 'ml-[18px]' : 'ml-[2px]'}`} />
                      </button>
                    </div>
                    {/* Bestimmte Tage: aktive Tage anzeigen */}
                    {habit.schedule?.frequencyType === 'custom' && habit.schedule?.daysOfWeek?.length > 0 && (
                      <div className="flex gap-1 mt-1.5">
                        {WEEKDAY_LABELS.map((label, i) => (
                          <span key={i} className={`w-7 h-7 rounded-lg text-[10px] font-semibold flex items-center justify-center ${
                            habit.schedule.daysOfWeek.includes(i)
                              ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                              : 'bg-slate-50 text-slate-300 border border-slate-200'
                          }`}>{label}</span>
                        ))}
                      </div>
                    )}
                    {editCalendarEnabled && habit.schedule?.frequencyType !== 'weekly' && (
                      <div className="mt-1.5 space-y-1.5 p-2 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-slate-500 w-12">Uhrzeit</span>
                          <input type="time" value={editCalendarTime} onChange={e => { setEditCalendarTime(e.target.value); setCalendarSaveError(''); setConflictsFullyResolved(false); }}
                            className="px-2 py-0.5 border border-slate-200 rounded text-xs bg-white" data-testid="edit-calendar-time" />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-slate-500 w-12">Dauer</span>
                          <input type="number" min={5} max={240} step={5} value={editCalendarDuration}
                            onChange={e => { setEditCalendarDuration(e.target.value === '' ? '' : Number(e.target.value)); setCalendarSaveError(''); }}
                            onClick={e => e.target.select()}
                            onBlur={() => { if (!editCalendarDuration || editCalendarDuration < 5) setEditCalendarDuration(5); }}
                            className="w-14 px-2 py-0.5 border border-slate-200 rounded text-xs text-center bg-white" data-testid="edit-calendar-duration" />
                          <span className="text-[10px] text-slate-400">Min.</span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-slate-500 w-12">Zeitraum</span>
                            <input type="number" min={1} max={24} value={editConsolidationMonths}
                              onChange={e => { setEditConsolidationMonths(e.target.value === '' ? '' : Number(e.target.value)); setCalendarSaveError(''); }}
                              onClick={e => e.target.select()}
                              onBlur={() => { if (!editConsolidationMonths || editConsolidationMonths < 1) setEditConsolidationMonths(3); }}
                              className="w-14 px-2 py-0.5 border border-slate-200 rounded text-xs text-center bg-white" data-testid="edit-consolidation" />
                            <span className="text-[10px] text-slate-400">Monate</span>
                            <button onClick={() => setShowConsolidationInfo(!showConsolidationInfo)} className="text-slate-300 hover:text-blue-400 transition-colors" type="button">
                              <Info className="w-3 h-3" />
                            </button>
                          </div>
                          {showConsolidationInfo && (
                            <p className="text-[10px] text-slate-500 bg-white rounded p-1.5 border border-blue-100 leading-relaxed">
                              Laut Forschung dauert es im Schnitt <strong>~66 Tage</strong> (ca. 2–3 Monate), bis eine neue Routine verinnerlicht ist.
                            </p>
                          )}
                        </div>
                        {conflictsFullyResolved && calendarConflict && !calendarConflict.includes('Habit') ? (
                          <div className="flex items-center gap-1.5 p-1.5 bg-green-50 rounded border border-green-200">
                            <span className="text-[10px] text-green-700 font-semibold">✓ Konflikte gelöst</span>
                          </div>
                        ) : (calendarConflict || calendarSaveError) ? (
                          <div className="flex items-center gap-1.5 p-1.5 bg-red-50 rounded border border-red-200">
                            <Info className="w-3 h-3 text-red-500 flex-shrink-0" />
                            <p className="text-[10px] text-red-600 flex-1">{calendarConflict || calendarSaveError}</p>
                            {calendarConflict && !calendarConflict.includes('Habit') && (
                              <button type="button"
                                onClick={() => { setConflictModalForShift(null); setShowConflictModal(true); }}
                                className="flex-shrink-0 text-[10px] font-bold text-white bg-blue-600 hover:bg-blue-700 px-2.5 py-1.5 rounded-lg shadow-sm transition-colors"
                                data-testid="open-conflict-modal-btn">
                                Lösen
                              </button>
                            )}
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                )}
            </div>
            </div>

            {/* Save Settings */}
            {calendarConflict && !conflictsFullyResolved && (
              <div className="flex items-start gap-1.5 p-2 bg-red-50 rounded-lg border border-red-200">
                <Info className="w-3.5 h-3.5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-600">{calendarConflict}</p>
              </div>
            )}
            {calendarConflict && conflictsFullyResolved && (
              <div className="flex items-start gap-1.5 p-2 bg-green-50 rounded-lg border border-green-200">
                <span className="text-xs text-green-700 font-semibold">✓ Konflikte gelöst – Speichern freigegeben</span>
              </div>
            )}
            <button
              onClick={() => {
                // Konflikt-Prüfung (Wochenkalender + andere Habits)
                if (calendarConflict && !conflictsFullyResolved) {
                  setCalendarSaveError(calendarConflict);
                  return;
                }
                const updated = { ...habit };
                updated.ifThen = { ...updated.ifThen, cueCustom: editCue || null };
                if (habit.type === 'performance') {
                  updated.tracking = {
                    ...updated.tracking,
                    unitLabel: editUnitLabel || 'mal',
                    minimumTarget: editMinTarget,
                    currentTarget: editCurTarget,
                    goalTarget: editGoalTarget ? Number(editGoalTarget) : null
                  };
                }
                updated.notifications = {
                  ...updated.notifications,
                  reminderEnabled: editReminderEnabled,
                  reminderTime: editReminderEnabled ? editReminderTime : null
                };
                // Schichten: Update schichtZeiten und lösche alte Kalender-Einträge
                if (habit.schedule?.frequencyType === 'schichten') {
                  updated.schedule = { ...updated.schedule, schichtZeiten: editSchichtZeiten };
                  updated.calendarReminder = { ...updated.calendarReminder, enabled: false };
                } else {
                  updated.calendarReminder = {
                    enabled: habit.schedule?.frequencyType !== 'weekly' && editCalendarEnabled,
                    time: editCalendarTime,
                    consolidationMonths: editConsolidationMonths || 3,
                    durationMinutes: editCalendarDuration || 15
                  };
                }
                // Alte manuell gelöschte Habit-Reminder-Einträge für dieses Habit zurücksetzen
                try {
                  const delKey = 'zielplanung_deleted_habit_reminders';
                  const deleted = JSON.parse(userStorage.getItem(delKey) || '[]');
                  const filtered = deleted.filter(id => !id.startsWith(`hr_${habit.id}_`));
                  userStorage.setItem(delKey, JSON.stringify(filtered));
                } catch {}
                if (pendingSkippedDates.length > 0) {
                  updated.skippedDates = [...(updated.skippedDates || []), ...pendingSkippedDates];
                }
                if (Object.keys(pendingDailyTimeOverrides).length > 0) {
                  updated.dailyTimeOverrides = { ...(updated.dailyTimeOverrides || {}), ...pendingDailyTimeOverrides };
                }
                // Pending Termin-Änderungen aus Konfliktauflösung jetzt in localStorage schreiben
                if (Object.keys(pendingAptChanges.updates).length > 0 || pendingAptChanges.deletes.length > 0) {
                  try {
                    const DELETED_KEY = 'zielplanung_deleted_habit_reminders';
                    const apts = JSON.parse(userStorage.getItem('zielplanung_appointments') || '[]');
                    const deletedHrIds = JSON.parse(userStorage.getItem(DELETED_KEY) || '[]');
                    const newDeletedHrIds = new Set(deletedHrIds);
                    const getKey = a => a.id != null ? String(a.id) : `${a.date}||${a.startTime}`;
                    const newApts = [];

                    for (const apt of apts) {
                      const k = getKey(apt);
                      if (pendingAptChanges.deletes.includes(k)) {
                        if (apt.isHabitReminder && typeof apt.id === 'string' && apt.id.startsWith('hr_')) {
                          newDeletedHrIds.add(apt.id);
                        }
                        continue;
                      }
                      if (pendingAptChanges.updates[k]) {
                        const upd = pendingAptChanges.updates[k];
                        if (apt.isHabitReminder && typeof apt.id === 'string' && apt.id.startsWith('hr_')) {
                          newDeletedHrIds.add(apt.id);
                          newApts.push({
                            ...upd,
                            id: `moved_hr_${Date.now()}_${Math.random().toString(36).slice(2)}`,
                            isHabitReminder: false,
                            habitId: undefined,
                          });
                        } else {
                          newApts.push({ ...apt, ...upd });
                        }
                        continue;
                      }
                      newApts.push(apt);
                    }

                    userStorage.setItem('zielplanung_appointments', JSON.stringify(newApts));
                    userStorage.setItem(DELETED_KEY, JSON.stringify([...newDeletedHrIds]));
                    window.dispatchEvent(new CustomEvent('burningteam-calendar-sync'));
                  } catch (e) {
                    console.error('Fehler beim Schreiben der Termin-Änderungen:', e);
                  }
                }
                updated.updatedAt = new Date().toISOString();
                onUpdate(updated);
                setShowSettings(false);
              }}
              className={`w-full py-2 text-white text-sm font-medium rounded-lg transition-colors ${(calendarConflict && !conflictsFullyResolved) ? 'bg-slate-300 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}
              disabled={!!(calendarConflict && !conflictsFullyResolved)}
              data-testid="save-settings-btn"
            >
              Einstellungen speichern
            </button>
          </div>
        )}
      </div>

      {/* Kalender-Konflikt Modal */}
      {showConflictModal && (
        <HabitCalendarConflictModal
          isOpen={showConflictModal}
          onClose={() => setShowConflictModal(false)}
          onDone={({ globalTime, globalDuration, skippedDates, dailyTimeOverrides, aptUpdates = {}, aptDeletes = [] }) => {
            if (!conflictModalForShift) {
              if (globalTime) setEditCalendarTime(globalTime);
              if (globalDuration) setEditCalendarDuration(globalDuration);
            } else {
              // Schicht: Zeit für diese Schicht übernehmen + als gelöst markieren
              if (globalTime) setEditSchichtZeiten(prev => ({ ...prev, [conflictModalForShift]: { ...prev[conflictModalForShift], time: globalTime } }));
              if (globalDuration) setEditSchichtZeiten(prev => ({ ...prev, [conflictModalForShift]: { ...prev[conflictModalForShift], duration: globalDuration } }));
              setResolvedShifts(prev => new Set([...prev, conflictModalForShift]));
            }
            if (skippedDates?.length > 0) setPendingSkippedDates(prev => [...prev, ...skippedDates]);
            if (dailyTimeOverrides && Object.keys(dailyTimeOverrides).length > 0) {
              setPendingDailyTimeOverrides(prev => ({ ...prev, ...dailyTimeOverrides }));
            }
            // Termin-Änderungen sammeln (werden erst beim "Einstellungen speichern" geschrieben)
            if (Object.keys(aptUpdates).length > 0 || aptDeletes.length > 0) {
              setPendingAptChanges(prev => ({
                updates: { ...prev.updates, ...aptUpdates },
                deletes: [...new Set([...prev.deletes, ...aptDeletes])],
              }));
            }
            setConflictsFullyResolved(true);
            setConflictRefreshKey(k => k + 1);
            setShowConflictModal(false);
          }}
          frequency={conflictModalForShift ? 'schichten' : habit.schedule?.frequencyType}
          habitTitle={habit.title}
          habitId={habit.id}
          schichtRhythmus={conflictModalForShift ? (habit.schedule?.schichtRhythmus ?? null) : undefined}
          habitTime={conflictModalForShift ? null : editCalendarTime}
          habitDuration={conflictModalForShift ? null : editCalendarDuration}
          daysOfWeek={habit.schedule?.daysOfWeek}
          schichtZeiten={conflictModalForShift
            ? { [conflictModalForShift]: editSchichtZeiten[conflictModalForShift] }
            : undefined}
        />
      )}

      {/* SchichtTagsModal für Bestimmte-Tage-Auswahl */}
      {showSchichtTagsInDetail && schichtTagsTargetInDetail && habit.schedule?.schichtRhythmus && (
        <SchichtTagsModal
          rhythmus={habit.schedule.schichtRhythmus}
          targetSchicht={schichtTagsTargetInDetail}
          selectedPositions={editSchichtZeiten[schichtTagsTargetInDetail]?.enabledCyclePositions || []}
          onSave={(positions) => {
            setEditSchichtZeiten(prev => ({
              ...prev,
              [schichtTagsTargetInDetail]: { ...prev[schichtTagsTargetInDetail], enabledCyclePositions: positions }
            }));
            setShowSchichtTagsInDetail(false);
          }}
          onClose={() => setShowSchichtTagsInDetail(false)}
        />
      )}

      {/* Actions */}
      <div className="bg-white rounded-xl p-4 border border-slate-100 space-y-2">
        <h3 className="text-sm font-semibold text-slate-700 mb-2">Aktionen</h3>
        {habit.status !== 'paused' ? (
          <button onClick={() => handleStatusChange('paused')} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">
            <Pause className="w-4 h-4" /> Pausieren
          </button>
        ) : (
          <button onClick={() => handleStatusChange('new')} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-emerald-600 hover:bg-emerald-50 rounded-lg">
            <Play className="w-4 h-4" /> Fortsetzen
          </button>
        )}
        <button onClick={() => handleStatusChange('archived')} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">
          <Archive className="w-4 h-4" /> Archivieren
        </button>
        {!showDeleteConfirm ? (
          <button onClick={() => setShowDeleteConfirm(true)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-lg">
            <Trash2 className="w-4 h-4" /> Löschen
          </button>
        ) : (
          <div className="flex gap-2">
            <button onClick={() => { onDelete(habit.id); onBack(); }} className="flex-1 py-2 bg-red-500 text-white text-sm rounded-lg">Endgültig löschen</button>
            <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 py-2 bg-slate-100 text-slate-600 text-sm rounded-lg">Abbrechen</button>
          </div>
        )}
      </div>
    </div>
  );
}
